import React from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import {
  EmojiEvents,
  Star,
  TrendingUp,
  CheckCircle,
  Lock,
  LocalFireDepartment
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  unlocked: boolean;
  progress?: number; // 0-100 for partial achievements
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface AchievementBadgeProps {
  achievement: Achievement;
  size?: 'small' | 'medium' | 'large';
  showProgress?: boolean;
}

const AchievementBadge: React.FC<AchievementBadgeProps> = ({
  achievement,
  size = 'medium',
  showProgress = true
}) => {
  const { name, description, icon, unlocked, progress, rarity } = achievement;

  const sizeMap = {
    small: { avatar: 40, text: 'body2' },
    medium: { avatar: 60, text: 'body1' },
    large: { avatar: 80, text: 'h6' }
  };

  const rarityColors = {
    common: '#4caf50',
    rare: '#2196f3',
    epic: '#9c27b0',
    legendary: '#ff9800'
  };

  const rarityGlow = {
    common: '0 0 10px rgba(76, 175, 80, 0.3)',
    rare: '0 0 15px rgba(33, 150, 243, 0.4)',
    epic: '0 0 20px rgba(156, 39, 176, 0.5)',
    legendary: '0 0 25px rgba(255, 152, 0, 0.6)'
  };

  return (
    <motion.div
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      whileHover={{ scale: 1.05 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 2,
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: unlocked ? `2px 4px 12px rgba(0,0,0,0.1), ${rarityGlow[rarity]}` : '2px 4px 8px rgba(0,0,0,0.1)',
          border: unlocked ? `2px solid ${rarityColors[rarity]}` : '2px solid #e0e0e0',
          opacity: unlocked ? 1 : 0.6,
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Rarity indicator */}
        <Chip
          label={rarity.toUpperCase()}
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            fontSize: '0.6rem',
            height: 20,
            backgroundColor: rarityColors[rarity],
            color: 'white',
            fontWeight: 'bold'
          }}
        />

        <Avatar
          sx={{
            width: sizeMap[size].avatar,
            height: sizeMap[size].avatar,
            backgroundColor: unlocked ? rarityColors[rarity] : 'grey.300',
            mb: 1,
            filter: unlocked ? 'none' : 'grayscale(100%)'
          }}
        >
          {unlocked ? icon : <Lock />}
        </Avatar>

        <Typography
          variant={sizeMap[size].text as any}
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            mb: 0.5,
            color: unlocked ? 'text.primary' : 'text.disabled'
          }}
        >
          {name}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            textAlign: 'center',
            color: unlocked ? 'text.secondary' : 'text.disabled',
            lineHeight: 1.2
          }}
        >
          {description}
        </Typography>

        {/* Progress indicator for partial achievements */}
        {showProgress && progress !== undefined && progress < 100 && (
          <Box sx={{ width: '100%', mt: 1 }}>
            <Box
              sx={{
                width: '100%',
                height: 4,
                backgroundColor: 'grey.200',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: rarityColors[rarity],
                  transition: 'width 0.3s ease'
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {progress}% Complete
            </Typography>
          </Box>
        )}

        {/* Unlock animation effect */}
        {unlocked && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none'
            }}
          >
            <LocalFireDepartment
              sx={{
                fontSize: 40,
                color: rarityColors[rarity],
                filter: 'drop-shadow(0 0 8px rgba(255, 152, 0, 0.6))'
              }}
            />
          </motion.div>
        )}
      </Box>
    </motion.div>
  );
};

// Predefined achievements
export const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Complete your first compliance question',
    icon: <Star />,
    unlocked: false,
    rarity: 'common'
  },
  {
    id: 'bronze_level',
    name: 'Bronze Achiever',
    description: 'Reach Bronze compliance level',
    icon: <EmojiEvents />,
    unlocked: false,
    rarity: 'common'
  },
  {
    id: 'silver_level',
    name: 'Silver Standard',
    description: 'Achieve Silver compliance rating',
    icon: <EmojiEvents />,
    unlocked: false,
    rarity: 'rare'
  },
  {
    id: 'gold_level',
    name: 'Gold Excellence',
    description: 'Reach Gold compliance level',
    icon: <EmojiEvents />,
    unlocked: false,
    rarity: 'epic'
  },
  {
    id: 'platinum_level',
    name: 'Platinum Elite',
    description: 'Achieve Platinum compliance status',
    icon: <EmojiEvents />,
    unlocked: false,
    rarity: 'legendary'
  },
  {
    id: 'complete_all',
    name: 'Completionist',
    description: 'Answer all compliance questions',
    icon: <CheckCircle />,
    unlocked: false,
    rarity: 'legendary'
  },
  {
    id: 'streak_7',
    name: 'Weekly Warrior',
    description: '7 day completion streak',
    icon: <TrendingUp />,
    unlocked: false,
    rarity: 'rare'
  }
];

export default AchievementBadge;